
/**
 * Module dependencies.
 */

import { Collapse } from '../collapse';
import { ReactNode, useEffect, useRef } from 'react';
import { color, media, states } from 'react-components/styles';
import { ifProp, switchProp, theme } from 'styled-tools';
import styled, { css } from 'styled-components';

/**
 * `ContentSize` type.
 */

type ContentSize = 'extraSmall' | 'medium' | 'small';

/**
 * `Size` type.
 */

type Size = 'medium' | 'small';

/**
 * Export `BaseDisclosureProps` type.
 */

export type BaseDisclosureProps = {
  children?: ReactNode;
  className?: string;
  contentSize?: ContentSize;
  disabled?: boolean;
  header: ReactNode;
  isOpen?: boolean;
  scrollOnOpen?: boolean;
  size?: Size;
  toggle?: () => void;
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div<{ disabled: boolean }>`
  background-color: ${color('white')};
  border: 1px solid ${color('gray200')};
  border-radius: ${theme('dimensions.borderRadius')}px;
  transition: opacity ${theme('animations.defaultTransition')};

  ${ifProp('disabled', css`
    opacity: 0.5;
  `)}
`;

/**
 * `Header` styled component.
 */

const Header = styled.button<{ size: Size }>`
  appearance: none;
  background: transparent;
  border: 0;
  color: inherit;
  cursor: pointer;
  text-align: inherit;
  width: 100%;

  ${switchProp('size', {
    medium: css`
      padding: 16px;

      ${media.min('md')`
        padding: 24px;
      `}
    `,
    small: css`
      padding: 12px;
    
      ${media.min('md')`
        padding: 16px;
      `}
    `
  })}

  ${states.action`
    outline: none;
  `}

  &:disabled {
    cursor: default;
    pointer-events: none;
  }
`;

/**
 * `Content` styled component.
 */

const Content = styled.div<{ size: ContentSize }>`
  ${switchProp('size', {
    extraSmall: css`
      padding: 0 12px 12px;

      ${media.min('md')`  
        padding: 16px;
      `}
    `,
    medium: css`
      padding: 0 16px 16px;

      ${media.min('md')`  
        padding: 6px 24px 24px;
      `}
    `,
    small: css`
      padding: 0 12px 24px;

      ${media.min('md')`  
        padding: 24px 16px;
      `}
    `
  })}
`;

/**
 * Export `BaseDisclosure` component.
 */

export function BaseDisclosure(props: BaseDisclosureProps): JSX.Element {
  const {
    children,
    contentSize = 'medium',
    disabled = false,
    header,
    isOpen,
    scrollOnOpen,
    size = 'medium',
    toggle,
    ...rest
  } = props;

  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!scrollOnOpen || !isOpen) {
      return;
    }

    const timeout = setTimeout(() => {
      if (ref.current && ref.current.scrollIntoView) {
        ref.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [isOpen, scrollOnOpen]);

  return (
    <Wrapper
      disabled={disabled}
      ref={ref}
      {...rest}
    >
      <Header
        aria-expanded={isOpen}
        disabled={disabled}
        onClick={toggle}
        size={size}
        type={'button'}
      >
        {header}
      </Header>

      {children && (
        <Collapse isOpen={isOpen}>
          <Content size={contentSize}>
            {children}
          </Content>
        </Collapse>
      )}
    </Wrapper>
  );
}
